import { useDispatch, useSelector } from 'react-redux';
import { parseAsString, useQueryStates } from 'nuqs';
import { stringify } from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RootState } from '../../state/store';
import { useEffect, useMemo, useCallback, useRef } from 'react';
import {
  buildFilterQueryParams,
  createCompanyDateSelectionPresets,
  validateUrlDateTimeFilters
} from '../../helpers/utils';
import {
  setDateTimeFilter,
  setUrlDateValidated
} from '../../state/features/filters/actions';
import { WHITELISTED_DASHBOARD_PATHS } from '../../helpers/constants';

interface CompanyData {
  business_end_time?: number;
  time_zone_offset?: string;
}

interface Dashboard {
  api_path: string;
}

interface DashboardDefaultsProps {
  companyData: CompanyData;
}

const DashboardDefaults = ({ companyData }: DashboardDefaultsProps) => {
  const [{ from: startRange, to: endRange }, setDateQuery] = useQueryStates(
    {
      from: parseAsString,
      to: parseAsString
    },
    { history: 'replace' }
  );
  const location = useLocation();
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const { defaultDashboard, dashboards } = useSelector((state: RootState) => state.dashboard) as {
    defaultDashboard: Dashboard | null;
    dashboards: Dashboard[];
  };
  const { dates, timeRange } = useSelector((state: RootState) => state.filters);
  const hasInitializedFiltersRef = useRef(false);

  const availableDashboardPaths = useMemo(
    () => dashboards.map((d) => `/${d.api_path}`),
    [dashboards]
  );

  const defaultPath = useMemo(() => {
    if (companyData && defaultDashboard) {
      return `/${defaultDashboard.api_path}`;
    }
    return '/';
  }, [companyData, defaultDashboard]);

  // Step 1: Validate and normalize the current path
  const getValidatedPath = useCallback(
    (currentPath: string) => {
      const isInAvailablePaths = availableDashboardPaths.some((path) =>
        currentPath.startsWith(path)
      );
      const isInWhitelistedPaths = WHITELISTED_DASHBOARD_PATHS.includes(currentPath);
      const isRootOrSSO = ['/', '/login/sso'].includes(currentPath);

      // If path is valid (in available or whitelisted), return it
      if (isInAvailablePaths || isInWhitelistedPaths) {
        return currentPath;
      }

      // If path is root/SSO or not found, return default path
      if (isRootOrSSO || (!isInAvailablePaths && !isInWhitelistedPaths)) {
        return defaultPath;
      }

      return currentPath;
    },
    [availableDashboardPaths, defaultPath]
  );

  // Step 2: Apply query params or redirect based on path type
  const handlePathRedirection = useCallback(
    (targetPath: string) => {
      const isInAvailablePaths = availableDashboardPaths.some((path) =>
        targetPath.startsWith(path)
      );
      const isInWhitelistedPaths = WHITELISTED_DASHBOARD_PATHS.includes(targetPath);

      if (isInWhitelistedPaths) {
        // For whitelisted paths, redirect without applying date time filters
        if (location.pathname !== targetPath || location.search) {
          navigateTo(targetPath);
        }
        return;
      }

      if (isInAvailablePaths && companyData) {
        const pathSegments = targetPath.split('/').filter(Boolean);
        const dashboardName = pathSegments[0] ?? '';
        const isSubReportPath = pathSegments[2] === 'report' && pathSegments.length >= 4;
        const shouldValidateSubRDateRange =
          isSubReportPath && ['inventory', 'operations'].includes(dashboardName);

        // Non-Sub-R report paths: allow fetches without re-running URL date validation
        if (targetPath.includes('report/') && !shouldValidateSubRDateRange) {
          dispatch(setUrlDateValidated(true));
          return;
        }

        // Mark URL dates as not yet validated so report fetches wait
        dispatch(setUrlDateValidated(false));

        // For available dashboard paths, apply URL query params date time filters
        const { today: todayPreset } = createCompanyDateSelectionPresets({
          businessEndTimeInSecs: companyData?.business_end_time,
          timezoneOffset: companyData?.time_zone_offset
        });

        const shouldUseReduxDefaults =
          hasInitializedFiltersRef.current && (!startRange || !endRange);
        const defaultDateRange = shouldUseReduxDefaults ? dates : todayPreset.date_range;
        const defaultTimeRange = shouldUseReduxDefaults ? timeRange : todayPreset.time_range;

        validateUrlDateTimeFilters(
          {
            defaultDateRange,
            defaultTimeRange,
            startRange,
            endRange
          },
          ({ startDateRange, endDateRange, startTimeRange, endTimeRange }) => {
            const queryParams = buildFilterQueryParams({
              dateRange: [startDateRange, endDateRange],
              timeRange: [startTimeRange, endTimeRange]
            });
            const newQueryParams = stringify(queryParams, { encode: true, sort: false });

            // Only update if path changed or query params are different
            if (location.pathname !== targetPath) {
              const nextPath = newQueryParams ? `${targetPath}?${newQueryParams}` : targetPath;
              navigateTo(nextPath);
            } else {
              setDateQuery(queryParams);
            }

            dispatch(
              setDateTimeFilter({
                dateRange: [startDateRange, endDateRange],
                timeRange: [startTimeRange, endTimeRange]
              })
            );
            dispatch(setUrlDateValidated(true));
            hasInitializedFiltersRef.current = true;
          }
        );
      }
    },
    [
      availableDashboardPaths,
      companyData,
      location.pathname,
      navigateTo,
      startRange,
      endRange,
      setDateQuery,
      dispatch
    ]
  );

  // Main effect that orchestrates the two-step process
  useEffect(() => {
    if (!defaultDashboard || !companyData) return;

    const currentPath = location.pathname;

    // Step 1: Validate the path
    const validatedPath = getValidatedPath(currentPath);

    // Step 2: Handle redirection and query params
    handlePathRedirection(validatedPath);
  }, [
    location.pathname,
    location.search,
    defaultDashboard,
    companyData,
    getValidatedPath,
    handlePathRedirection
  ]);

  return null;
};

export default DashboardDefaults;
